import { useState, useMemo } from 'react';
import type { SyntheticEvent } from 'react';
import { useGetSubTopicById } from '../../api/useGetSubTopicById';
import { useGetSuggestedSubTopics } from '../../api/useGetSuggestedSubTopics';
import type { SubTopicOption } from '../../types/subTopicAutocomplete.types';

export type UseSubTopicAutocompleteProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  excludeIds?: string[];
};

export function getTopicName(option: SubTopicOption | null | undefined): string | null {
  if (!option?.topics) {
    return null;
  }
  if (Array.isArray(option.topics)) {
    return option.topics[0]?.name || null;
  }
  return option.topics.name || null;
}

export function useSubTopicAutocomplete(props: UseSubTopicAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const { data: suggestedSubTopics, isLoading: isLoadingSuggestions } = useGetSuggestedSubTopics(inputValue, true);
  const { data: selectedSubTopic, isLoading: isLoadingSelected } = useGetSubTopicById(props.value || undefined);

  const excludeSet = useMemo(function () {
    return new Set(props.excludeIds ?? []);
  }, [props.excludeIds]);

  const options = useMemo(function () {
    const list: SubTopicOption[] = suggestedSubTopics
      ? (suggestedSubTopics as SubTopicOption[]).filter(function (s) {
          return !excludeSet.has(s.id);
        })
      : [];

    if (
      selectedSubTopic &&
      !excludeSet.has(selectedSubTopic.id) &&
      !list.some(function (s) {
        return s.id === selectedSubTopic.id;
      })
    ) {
      list.push(selectedSubTopic as SubTopicOption);
    }

    return list;
  }, [suggestedSubTopics, selectedSubTopic, excludeSet]);

  const isLoading = isLoadingSuggestions || (!!props.value && isLoadingSelected);

  function handleChange(_event: SyntheticEvent, newValue: SubTopicOption | string | null) {
    if (!newValue || typeof newValue === 'string') {
      props.onChange(null);
      return;
    }
    props.onChange(newValue.id);
  }

  function handleInputChange(_event: SyntheticEvent, newInputValue: string) {
    setInputValue(newInputValue);
  }

  function getOptionLabel(option: SubTopicOption | string): string {
    if (typeof option === 'string') {
      return option;
    }
    return option.name;
  }

  function filterOptions(optionsToFilter: SubTopicOption[], state: { inputValue: string }): SubTopicOption[] {
    const query = state.inputValue.trim().toLowerCase();
    if (!query) {
      return optionsToFilter;
    }

    return optionsToFilter.filter(function (option) {
      const nameMatch = option.name.toLowerCase().includes(query);
      const topicName = getTopicName(option);
      const topicMatch = topicName ? topicName.toLowerCase().includes(query) : false;
      return nameMatch || topicMatch;
    });
  }

  return {
    inputValue,
    options,
    selectedSubTopic: (selectedSubTopic as SubTopicOption | undefined) || null,
    isLoading,
    handleChange,
    handleInputChange,
    getOptionLabel,
    filterOptions,
  };
}
